import React, { useEffect, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useForm } from "react-hook-form";

import { useDelayedRender } from "hooks";
import firebase from "firebaseFactory";
import { useAuthDataContext } from "modules/auth";
import { MessagePage } from "modules/layout/MessagePage";
import logo from "logo_black.svg";

type FormData = {
  email: string;
  password: string;
};
type LocationState = {
  from: { pathname: string };
};

function useEffectRedirectIfAlreadyAuthenticated(redirect: VoidFunction) {
  const authState = useAuthDataContext();
  useEffect(() => {
    if (authState.authenticated) {
      redirect();
    }
  }, [redirect, authState.authenticated]);
}

// this is checked every time, but will pass quickly when we're not
// waiting on a redirect result
function useEffectRedirectAfterReturnFromGoogle(
  redirectCallback: VoidFunction,
  errorCallback: (error: string) => void
) {
  const [waitingForRedirectResult, setWaitingForRedirectResult] = useState(
    true
  );
  useEffect(() => {
    // used to prevent this triggering state changes whilst dismounted
    let ignore = false;
    firebase
      .auth()
      .getRedirectResult()
      .then(
        (result) => {
          if (result.credential) {
            redirectCallback();
          }
        },
        (error) => errorCallback(error)
      )
      .then(() => ignore || setWaitingForRedirectResult(false));
    return () => {
      ignore = true;
    };
  }, [redirectCallback, errorCallback]);
  return { waitingForRedirectResult };
}

export const SignInPage = () => {
  const [serverError, setServerError] = useState<string>();
  const { register, handleSubmit, errors } = useForm<FormData>();
  const delayedRender = useDelayedRender(100);

  const history = useHistory();
  const location = useLocation<LocationState>();

  const returnToOriginalUrl = () => {
    const returnToUrlParameter = location.search.startsWith("?returnTo=")
      ? decodeURIComponent(location.search.substr(10))
      : "/";
    history.replace(returnToUrlParameter);
  };

  const { waitingForRedirectResult } = useEffectRedirectAfterReturnFromGoogle(
    returnToOriginalUrl,
    setServerError
  );
  useEffectRedirectIfAlreadyAuthenticated(returnToOriginalUrl);

  const onClickSubmit = handleSubmit(({ email, password }) => {
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(
        () => returnToOriginalUrl(),
        (error) => setServerError(error.message)
      );
  });

  const onClickSignInWithGoogle = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({
      //'hd': 'relectrify.com' // this is not enforced
    });
    await firebase.auth().signInWithRedirect(provider);
  };

  if (waitingForRedirectResult)
    return delayedRender(() => (
      <MessagePage message="Please wait. Authenticating..." />
    ));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full py-4 px-6">
        <img className="mx-auto h-20 w-auto my-8" src={logo} alt="" />
        <form
          className="bg-white rounded px-8 pt-6 pb-8 mb-4"
          noValidate
          onSubmit={onClickSubmit}
        >
          <div className="mb-4">
            {JSON.stringify(serverError)}
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="email"
            >
              Email address
            </label>
            <input
              name="email"
              id="email"
              type="email"
              ref={register({ required: true })}
              className={`${
                errors.email ? "border-red-500 " : ""
              } shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
              placeholder="Email address"
            />
            {errors.email && (
              <p className="text-red-500 text-xs italic">
                Please enter a email.
              </p>
            )}
          </div>
          <div className="mb-8">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="password"
            >
              Password
            </label>
            <input
              name="password"
              id="password"
              type="password"
              ref={register({ required: true })}
              className={`${
                errors.password ? "border-red-500 " : ""
              } shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
              placeholder="******************"
            />
            {errors.password && (
              <p className="text-red-500 text-xs italic">
                Please enter a password.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <input
              type="submit"
              value="Sign In"
              className="bg-blue-500 cursor-pointer hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            />
            <Link
              className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800"
              to={`/forgotten-password`}
            >
              Forgot Password?
            </Link>
          </div>
        </form>
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm leading-5">
              <span className="px-2 bg-gray-100 text-gray-500">or</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={onClickSignInWithGoogle}
              type="submit"
              className="relative block w-full py-2 px-3 border border-transparent rounded-md text-white font-semibold bg-gray-800 hover:bg-gray-700 focus:bg-gray-900 focus:outline-none focus:shadow-outline sm:text-sm sm:leading-5"
            >
              <span className="absolute left-0 inset-y pl-3">
                <svg
                  className="h-5 w-5 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
              Log in with Google
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
